import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { TranslateModule, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { LegalService } from '../../../services/legal.service';
import { LegalArticle } from '../../../models/legal-article.model';
import { Loading } from '../../layouts/loading/loading';

@Component({
  selector: 'app-loi-detail',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslateModule, TranslatePipe, Loading],
  templateUrl: './loi-detail.html',
  styleUrl: './loi-detail.css',
})
export class LoiDetailComponent implements OnInit {
  article: LegalArticle | null = null;
  loading = true;
  error   = '';

  /* clé du contenu affiché : 'fr' | 'en' | 'ar' */
  activeLang: 'fr' | 'en' | 'ar' = 'fr';

  readonly categoryColors: Record<string, string> = {
    loi:        'cat--loi',
    decret:     'cat--decret',
    arrete:     'cat--arrete',
    circulaire: 'cat--circulaire',
    reglement:  'cat--reglement',
  };

  readonly categoryLabels: Record<string, Record<string, string>> = {
    loi:        { fr: 'Loi',        en: 'Law',        ar: 'قانون'  },
    decret:     { fr: 'Décret',     en: 'Decree',     ar: 'مرسوم' },
    arrete:     { fr: 'Arrêté',     en: 'Order',      ar: 'قرار'  },
    circulaire: { fr: 'Circulaire', en: 'Circular',   ar: 'منشور' },
    reglement:  { fr: 'Règlement',  en: 'Regulation', ar: 'نظام'  },
  };

  constructor(
    private route: ActivatedRoute,
    private legalService: LegalService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    // Initialise la langue de l'interface
    const lang = this.translate.getCurrentLang();
    this.activeLang = (lang === 'ar' || lang === 'en') ? lang : 'fr';

    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (!id) { this.error = 'loi_erreur_id'; this.loading = false; return; }

    this.legalService.getArticle(id).subscribe({
      next: res => {
        this.article = res.data ?? (res as any);
        this.loading = false;
      },
      error: (err) => {
        this.error   = err?.status === 404 ? 'loi_introuvable' : 'loi_erreur_chargement';
        this.loading = false;
      },
    });
  }

  /* ── helpers ────────────────────────────────────────────────────── */
  tf(field: { fr: string; en: string; ar: string } | null | undefined): string {
    if (!field) return '';
    return (field as any)[this.activeLang] || field.fr || field.en || '';
  }

  tfInterface(field: { fr: string; en: string; ar: string } | null | undefined): string {
    if (!field) return '';
    const lang = this.translate.getCurrentLang();
    return (field as any)[lang] || field.fr || field.en || '';
  }

  getCategoryLabel(): string {
    if (!this.article) return '';
    const entry = this.categoryLabels[this.article.category];
    return entry?.[this.activeLang] || entry?.['fr'] || this.article.category;
  }

  getCategoryColor(): string {
    return this.categoryColors[this.article?.category ?? ''] ?? 'cat--default';
  }

  formatDate(d: string): string {
    try { return new Date(d).toLocaleDateString('fr-FR', { day:'2-digit', month:'long', year:'numeric' }); }
    catch { return d; }
  }

  hasLang(field: { fr: string; en: string; ar: string } | null | undefined, lang: string): boolean {
    return !!((field as any)?.[lang]);
  }
}
